import './OfferCritDetails.scss';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '@atoms/Button';
import clsx from 'clsx';
import { MdClose } from 'react-icons/md';
import Modal from '@atoms/Modal';
import {
    CritType,
    IOfferAnswerItem,
    IOfferAnswerSetting,
    IOfferCrit,
    ITableDemandItem,
    RatingScale,
} from '@models/winnerSelection/ITableDemand';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@molecules/Notifications';
import Hint from '@atoms/Hint';
import { round } from 'mathjs';
import { IOfferCritDetailsFormData } from './IOfferCritDetailsFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@controls/HookFormProvider';
import TextareaControl from '@controls/TextareaControl';
import {
    RatingScaleDefault,
    RatingScalePirSmr,
    RatingScalePSTZ,
    RatingScaleSimTum,
    RatingScaleTMC,
} from './RatingScales';
import ColoredLine from '@atoms/ColoredLine';
import CheckboxControl from '@controls/CheckboxControl';
import { IOption } from '@/types';
import { SelectControl } from '@controls/index';

export interface IOfferCritDetailsProps {
    tableKey: string;
    isEdit: boolean;
    demandId: string;
    offerId: string;
    disabledHint?: string;
    ratingScaleHints?: Record<string, string> | null;
    customScaleCrits?: string[] | null;
    editButtonName?: string | null;
    allowGroupActions?: boolean;
    value: IOfferCrit;
    rowData: ITableDemandItem;
    onSubmit?: (value: IOfferCritDetailsFormData) => void;
    offerAnswerSettings?: IOfferAnswerSetting[] | null;
}

function evalPredicateWithAnswer(
    requiredPredicate: string | null | undefined,
    answer: string | null | undefined,
): boolean {
    if (!requiredPredicate?.trim()) return false;

    // Подставляем {answer} => "строка" c экранированием
    const expr = requiredPredicate.replace(/\{answer\}/g, JSON.stringify(answer ?? ''));

    try {
        // eslint-disable-next-line no-eval
        const result = eval(`(function(){ "use strict"; return (${expr}); })()`);
        return !!result;
    } catch {
        return false;
    }
}

const checkCommentRequired = (
    scale: RatingScale,
    score: number | null,
    critType: Exclude<CritType, null>,
    critName: string,
    answers: IOfferAnswerItem[],
    customScaleCrits?: string[] | null,
    requiredPredicate?: string | null,
    answer?: string | null,
) => {
    if (critType === 'info') return false;

    if (critType === 'answer') {
        // считаем по предикату (через eval); если предиката нет - по атрибуту выбранного ответа, если нет - не требуем комментарий
        return requiredPredicate
            ? evalPredicateWithAnswer(requiredPredicate, answer)
            : (answers.find((a) => a.value === answer)?.commentRequired ?? false);
    }

    if (!customScaleCrits?.includes(critName)) return false;
    if (score === null) return false;
    return score < 3.0;
};

const getEffectiveCritType = (row: ITableDemandItem): Exclude<CritType, null> =>
    row.critType ?? (row.isInfoCrit ? 'info' : 'score');

const isInList = (val: string | null | undefined, list?: IOfferAnswerItem[]) =>
    !!val && !!list?.some((a) => a.value === val);

const OfferCritDetails = ({
    tableKey,
    isEdit,
    disabledHint,
    ratingScaleHints,
    customScaleCrits,
    editButtonName,
    allowGroupActions = false,
    demandId,
    offerId,
    value,
    rowData,
    onSubmit,
    offerAnswerSettings,
}: IOfferCritDetailsProps) => {
    const [showModal, setShowModal] = useState<boolean>(false);
    const [error, setError] = useState<string>();

    const critType = getEffectiveCritType(rowData);

    const methods = useForm<IOfferCritDetailsFormData>({ mode: 'onBlur' });
    const { isSubmitting, isDirty } = methods.formState;

    // авторасчёт рейтинга
    const watchCalcValues = useWatch({ name: ['offerCrit.score', 'weight'], control: methods.control });

    // текущее значение ответа
    const currentAnswer = useWatch({ name: 'offerCrit.answer', control: methods.control });
    const answerSelect = useWatch({ name: 'offerCrit.answerSelect', control: methods.control });

    // настройка по ключу answerType
    const answerSetting = useMemo(() => {
        if (critType !== 'answer') return null;
        if (!rowData.answerType || !offerAnswerSettings) return null;
        return offerAnswerSettings.find((s) => s.key === rowData.answerType) ?? null;
    }, [critType, rowData.answerType, offerAnswerSettings]);

    // опции для SelectControl (answer)
    const answerOptions: IOption[] | undefined = useMemo(() => {
        if (answerSetting?.type !== 'select') return undefined;
        const base = (answerSetting.answers ?? []).map<IOption>((a) => ({ value: a.value, label: a.value }));
        return answerSetting.allowCustomAnswer ? [...base, { value: '__custom__', label: 'Другое…' }] : base;
    }, [answerSetting]);

    // текущее значение селекта (конверсия из строки ответа)
    const selectValueOptions: IOption[] | undefined = useMemo(() => {
        if (!answerOptions) return undefined;
        if (!answerSetting) return undefined;

        // если ответ из списка - выбираем его; если нет и разрешён кастом - выбираем "Другое…"
        const inList = isInList(currentAnswer, answerSetting.answers);
        if (inList) {
            const found = answerOptions.find((o) => o.value === currentAnswer);
            return found ? [found] : [];
        }
        if (answerSetting.allowCustomAnswer) {
            const customOpt = answerOptions.find((o) => o.value === '__custom__');
            return customOpt ? [customOpt] : [];
        }
        return [];
    }, [answerOptions, answerSetting, currentAnswer]);

    // инициализация полей формы при открытии/смене значения
    useEffect(() => {
        if (critType !== 'answer' || answerSetting?.type !== 'select') return;

        const inList = !!answerSetting.answers?.some((a) => a.value === currentAnswer);
        // если ответ из списка - выставляем селект на него, иначе на '__custom__' (если разрешён кастом)
        const initialSelect = inList ? (currentAnswer ?? '') : answerSetting.allowCustomAnswer ? '__custom__' : '';

        // инициализируем answerSelect, если ещё не поставлен
        const sel = methods.getValues('offerCrit.answerSelect') as string | undefined;
        if (!sel) {
            methods.setValue('offerCrit.answerSelect', initialSelect, { shouldDirty: false });
        }
        // если ответ не из списка и кастом разрешён - убедимся, что текст не потеряется
        if (!inList && answerSetting.allowCustomAnswer && (currentAnswer ?? '') === '') {
            methods.setValue('offerCrit.answer', '', { shouldDirty: false });
        }
    }, [showModal, critType, answerSetting, currentAnswer, methods]);

    // синхронизация: при выборе предустановленной опции копируем её в offerCrit.answer
    useEffect(() => {
        if (critType !== 'answer' || answerSetting?.type !== 'select') return;
        const sel = methods.getValues('offerCrit.answerSelect') as string | undefined;
        if (!sel) return;

        if (sel !== '__custom__') {
            // выбран вариант из списка - пишем его в текст ответа
            methods.setValue('offerCrit.answer', sel, { shouldDirty: true });
        } else {
            // выбран "Другое…" - не трогаем текст; пользователь редактирует offerCrit.answer вручную
        }
    }, [showModal, answerSelect, critType, answerSetting, methods]);

    // требование комментария
    const [commentRequired, setCommentRequired] = useState(() =>
        checkCommentRequired(
            rowData.ratingScale,
            value?.score ?? null,
            critType,
            rowData.critName,
            answerSetting?.answers ?? [],
            customScaleCrits,
            rowData.commentRequiredPredicate,
            value?.answer ?? null,
        ),
    );

    useEffect(() => {
        const values = methods.getValues();
        if (values.offerCrit && values.offerCrit.score !== null && values.weight !== null) {
            const newRating = round(values.offerCrit.score * values.weight, 4);
            methods.setValue('offerCrit.rating', newRating, { shouldDirty: true });
        }

        const isCommentReq = checkCommentRequired(
            rowData.ratingScale,
            values?.offerCrit?.score ?? null,
            critType,
            rowData.critName,
            answerSetting?.answers ?? [],
            customScaleCrits,
            rowData.commentRequiredPredicate,
            currentAnswer ?? null,
        );

        setCommentRequired(isCommentReq);
    }, [
        watchCalcValues,
        currentAnswer,
        critType,
        rowData.commentRequiredPredicate,
        rowData.ratingScale,
        rowData.critName,
        customScaleCrits,
        answerSetting?.answers,
    ]);

    useEffect(() => {
        const defaultValue: IOfferCritDetailsFormData = {
            offerCrit: value,
            critId: rowData.critId,
            weight: rowData.critWeight,
        };
        methods.reset(defaultValue);
    }, [value, showModal]);

    const onClick = () => setShowModal(!showModal);

    const onFormSubmit = async (form: IOfferCritDetailsFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferCrit(offerId, demandId, tableKey, form)
            .then(() => {
                sendNotification({ message: 'Данные успешно сохранены', variant: 'green' });
                setShowModal(!showModal);
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const errorLabel = error && (
        <div className="offer-crit-details__error">
            <Hint title={error} variant="red" />
        </div>
    );

    const ModalHeader = () => (
        <div className={clsx('offer-crit-details-modal__header')}>
            <div className={clsx('offer-crit-details-modal__header-text')}>
                <span>{`${rowData.critName}`}</span>
            </div>
            <div className={clsx('offer-crit-details-modal__header-close')}>
                <Button
                    buttonType="text"
                    textColor="neutral"
                    size="xs"
                    aria-label="Закрыть окно"
                    onClick={() => setShowModal(false)}
                    startAdornment={<MdClose size="24" />}
                />
            </div>
        </div>
    );

    const ModalFooter = () => (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-crit-details-form"
                preloader={isSubmitting}
                className={clsx('offer-crit-details__button', 'offer-crit-details__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                Сохранить
            </Button>
        </div>
    );

    const RatingScaleBlock = () => {
        if (!customScaleCrits?.includes(rowData.critName))
            return <RatingScaleDefault className="offer-crit-details__column" isEdit={isEdit} methods={methods} />;

        const hint = ratingScaleHints?.[rowData.ratingScale];

        switch (rowData.ratingScale) {
            case 'simtum':
                return (
                    <RatingScaleSimTum
                        className="offer-crit-details__column"
                        isEdit={isEdit}
                        methods={methods}
                        hint={hint}
                    />
                );
            case 'pirsmr':
                return (
                    <RatingScalePirSmr
                        className="offer-crit-details__column"
                        isEdit={isEdit}
                        methods={methods}
                        hint={hint}
                    />
                );
            case 'pstz':
                return (
                    <RatingScalePSTZ
                        className="offer-crit-details__column"
                        isEdit={isEdit}
                        methods={methods}
                        hint={hint}
                    />
                );
            case 'tmcws':
                return (
                    <RatingScaleTMC
                        className="offer-crit-details__column"
                        isEdit={isEdit}
                        methods={methods}
                        hint={hint}
                    />
                );
            default:
                return <RatingScaleDefault className="offer-crit-details__column" isEdit={isEdit} methods={methods} />;
        }
    };

    return (
        <>
            <div className={clsx('offer-crit-details')} onClick={onClick}>
                <Button type="button" aria-label="Показать детали" buttonType={'secondary'} onClick={onClick}>
                    {editButtonName ?? 'Показать детали'}
                </Button>
            </div>

            {showModal ? (
                <Modal size="xl" header={<ModalHeader />} footer={isEdit ? <ModalFooter /> : null}>
                    {errorLabel}

                    {!isEdit && disabledHint && (
                        <div className={'offer-crit-details__hint'}>
                            <Hint title={disabledHint} variant="yellow" maxWidth="100%" />
                        </div>
                    )}

                    <FormProviderControl
                        formMethods={methods}
                        id="offer-crit-details-form"
                        className="offer-crit-details__form"
                        onSubmit={onFormSubmit}
                    >
                        <HookFormProvider controller={Controller}>
                            <div className="offer-crit-details__content">
                                {critType === 'score' && (
                                    <div className="offer-crit-details__row">
                                        <RatingScaleBlock />
                                    </div>
                                )}

                                {critType === 'answer' && (
                                    <div className="offer-crit-details__row">
                                        {answerSetting?.type === 'select' && answerOptions ? (
                                            <>
                                                <SelectControl
                                                    label="Ответ"
                                                    name="offerCrit.answerSelect"
                                                    readOnly={true}
                                                    required={false}
                                                    disabled={!isEdit || rowData.answerReadonly}
                                                    highlightMenuItems={false}
                                                    options={answerOptions}
                                                    formState={methods.formState}
                                                    control={methods.control}
                                                    setValue={(opts: IOption[]) => {
                                                        methods.setValue('offerCrit.answer', '', { shouldDirty: true });
                                                        return opts?.[0]?.value ?? '';
                                                    }}
                                                    getValue={(opts: IOption[], value: any) => {
                                                        if (!opts) return [];
                                                        const match = opts.find((o) => o.value === value);
                                                        return match ? [match] : [];
                                                    }}
                                                />

                                                {answerSetting.allowCustomAnswer &&
                                                    selectValueOptions?.[0]?.value === '__custom__' && (
                                                        <TextareaControl
                                                            label="Свой ответ"
                                                            name="offerCrit.answer"
                                                            readOnly={!isEdit || rowData.answerReadonly}
                                                            disabled={!isEdit || rowData.answerReadonly}
                                                            autoResize={true}
                                                            formState={methods.formState}
                                                            control={methods.control}
                                                        />
                                                    )}
                                            </>
                                        ) : (
                                            <TextareaControl
                                                label="Ответ"
                                                name="offerCrit.answer"
                                                readOnly={!isEdit || rowData.answerReadonly}
                                                disabled={!isEdit || rowData.answerReadonly}
                                                autoResize={true}
                                                formState={methods.formState}
                                                control={methods.control}
                                            />
                                        )}
                                    </div>
                                )}

                                <div className="offer-crit-details__row">
                                    <TextareaControl
                                        label={critType === 'score' ? 'Обоснование' : 'Комментарий'}
                                        name="offerCrit.description"
                                        readOnly={!isEdit}
                                        disabled={!isEdit}
                                        required={commentRequired}
                                        autoResize={true}
                                        rules={{
                                            required: { value: commentRequired, message: 'Обязательное поле' },
                                        }}
                                        formState={methods.formState}
                                        control={methods.control}
                                    />
                                </div>

                                {allowGroupActions && isEdit && (
                                    <div
                                        className={clsx(
                                            'offer-crit-details__row',
                                            'offer-crit-details__group-actions-row',
                                        )}
                                    >
                                        <ColoredLine color="var(--neutral-20)" />
                                        <CheckboxControl
                                            label="На все предложения поставщика"
                                            description="Введенные сведения по критерию будут также перенесены на все предложения поставщика"
                                            name="offerCrit.applyToAllSupplierOffers"
                                            formState={methods.formState}
                                            control={methods.control}
                                            className="offer-crit-details__allSupplierOffers"
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    methods.setValue('offerCrit.applyToAllLotOffers', false, {
                                                        shouldDirty: false,
                                                    });
                                                }
                                            }}
                                        />
                                        <CheckboxControl
                                            label="На все предложения по лоту/позиции"
                                            description="Введенные сведения по критерию будут также перенесены на все предложения, поданные на лот/позицию"
                                            name="offerCrit.applyToAllLotOffers"
                                            formState={methods.formState}
                                            control={methods.control}
                                            className="offer-crit-details__applyToAllLotOffers"
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                if (checked) {
                                                    methods.setValue('offerCrit.applyToAllSupplierOffers', false, {
                                                        shouldDirty: false,
                                                    });
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </HookFormProvider>
                    </FormProviderControl>
                </Modal>
            ) : null}
        </>
    );
};

export default OfferCritDetails;
